import React, { useCallback, useEffect, useState } from "react";
import { loadTrailers } from "../../ducks/data/trailers";
import { addRoute } from "../../ducks/routes";
import { loadDriversActivitiesForRouteIfNotLoaded } from "../../ducks/data/driver-activities";
import { loadDrivers } from "../../ducks/data/drivers";
import { loadOrgSettings } from "../../ducks/data/org-settings";
import { useAppDispatch, useSelector } from "../../redux-store";
import { useTranslation } from "react-i18next";
import { selectRouteIds } from "../../ducks/data/route-views/selectors";
import RoutePageColumn from "../DispatchPage/RoutePageColumn";
import { DoraButton } from "dora-shared/frontend";
import { Typography } from "@mui/material";
import { loadUndispatchedCargoViews } from "../../ducks/data/cargo-views";
import { loadRouteViewModels } from "../../ducks/data/route-views";
import LiteVersionMiddleColumn from "./LiteVersionMiddleColumn";
import { useShowCargoModal, useFeature } from "../../hooks";
import { selectRouting } from "../../ducks/data/routing/selectors";
import LiteVersionCargoList from "./LiteVersionCargoList";

const LiteVersionSingleRoute = () => {
  const dispatch = useAppDispatch();
  const [addRouteInProgress, setAddRouteInProgress] = useState(false);
  const [routeViewsLoaded, setRouteViewsLoaded] = useState(false);
  const routing = useSelector(selectRouting());
  const { t } = useTranslation("dispatching");
  const openCargoModal = useShowCargoModal();

  const loadAllRoutes = useCallback(() => {
    setRouteViewsLoaded(false);
    dispatch(loadRouteViewModels())
      .unwrap()
      .then(() => {
        setRouteViewsLoaded(true);
      })
      .catch(() => {
        // The loadRouteViewModels thunk has already reported it and shown the error toast.
      });
  }, [dispatch, setRouteViewsLoaded]);

  useEffect(() => {
    dispatch(loadTrailers());
    loadAllRoutes();
    dispatch(loadUndispatchedCargoViews());
    dispatch(loadDrivers());
    dispatch(loadOrgSettings());
  }, [dispatch, loadAllRoutes]);

  const routeViewsIds = useSelector(selectRouteIds);
  // Neither route_views.stopStatuses nor .driverLogins has a historic
  // backfill, so this fetch stays on until BOTH flags are toggled on for an
  // org (likely months out) — until then, at least one of stop status /
  // login status still needs the activity log.
  const stopStatusFromRouteView = useFeature("stop-status-from-route-view");
  const driverLoginFromRouteView = useFeature("driver-login-from-route-view");

  useEffect(() => {
    if (stopStatusFromRouteView && driverLoginFromRouteView) {
      return;
    }
    for (const routeId of routeViewsIds) {
      dispatch(loadDriversActivitiesForRouteIfNotLoaded(routeId));
    }
  }, [
    routeViewsIds,
    dispatch,
    stopStatusFromRouteView,
    driverLoginFromRouteView,
  ]);

  const onCreateRouteClick = () => {
    setAddRouteInProgress(true);
    dispatch(addRoute({ intendedStartDate: null }))
      .unwrap()
      .then(() => {
        loadAllRoutes();
      })
      .catch(() => {
        // The addRoute thunk has already reported it and shown the error toast.
      })
      .finally(() => {
        setAddRouteInProgress(false);
      });
  };

  if (!routeViewsLoaded) {
    return null;
  }

  if (routeViewsIds.length > 1) {
    return (
      <div
        className="flex flex-col items-center gap-4 justify-center"
        style={{ height: "60%" }}
      >
        {t("liteVersion.errorContactSupport")}
      </div>
    );
  }

  if (routeViewsIds.length === 0) {
    return (
      <div
        className="flex flex-col items-center gap-4 justify-center"
        style={{ height: "60%" }}
      >
        <Typography variant="h2">
          {/* {t("liteVersion.noRoutes")} */}
          1. Opret rute
        </Typography>
        <Typography variant="h2">
          {/* {t("liteVersion.noRoutes")} */}
          2. Tilføj ordrer og evt. viapunkter
        </Typography>
        <Typography variant="h2">
          {/* {t("liteVersion.noRoutes")} */}
          3. Tryk "gem rute og luk"
        </Typography>
        <DoraButton
          id="add-co2-route-button"
          variant="primary-filled"
          disabled={addRouteInProgress}
          onClick={onCreateRouteClick}
        >
          {t("liteVersion.start")}
        </DoraButton>
      </div>
    );
  }

  const routeViewId = routeViewsIds[0];

  return (
    <div className="flex gap-6 px-2 py-8 h-full bg-white">
      <div className="flex-1 p-2">
        <Typography variant="h1">
          {t("liteVersion.shipmentsOnRouteHeading")}
        </Typography>
        <br />
        <DoraButton
          variant="primary-filled"
          onClick={() => openCargoModal("new", routeViewId)}
        >
          {t("liteVersion.addShipmentButton")}
        </DoraButton>
        <br />
        {/* <br /> */}
        {/* <Typography variant="h2">
          {t("liteVersion.shipmentsOnRouteHeading")}
        </Typography> */}
        <br />
        <LiteVersionCargoList routeId={routeViewId} />
      </div>
      <div className="flex-1 py-2 px-6 bg-gray-100">
        {routing && <LiteVersionMiddleColumn routeId={routeViewId} />}
      </div>
      <div className="flex-1">
        <RoutePageColumn routeId={routeViewId} />
      </div>
    </div>
  );
};

export default LiteVersionSingleRoute;
